import { useEffect } from 'react';
import logger from 'ws-scripts/modules/logger';
import useEventBus from './useEventBus';
import { scrollToUDS } from '../helpers';

let hasExecuted = false;

export const __resetHasExecuted = () => {
	hasExecuted = false;
};

const waitForTogglesScript = () => {
	return new Promise((resolve, reject) => {
		const TIMEOUT_MS = 20000;
		
		const isTogglesScriptLoaded = () => 
			Array.from(document.scripts).some((script) =>
				script.src.includes('makemydeal.com/global-toggles.js')
			);

		if (isTogglesScriptLoaded()) {
			resolve();
			return;
		}

		let timeoutId;
		const observer = new MutationObserver(() => {
			if (isTogglesScriptLoaded()) {
				clearTimeout(timeoutId);
				observer.disconnect();
				resolve();
			}
		});

		timeoutId = setTimeout(() => {
			observer.disconnect();
			reject(new Error('Timeout waiting for toggles script'));
		}, TIMEOUT_MS);

		observer.observe(document.documentElement, {
			childList: true,
			subtree: true
		});
	});
};

const waitForUDSConfigCompleted = (eventBus) => {
	return new Promise((resolve) => {
		const TIMEOUT_MS = 10000;
		let timeoutId;
		
		const handleConfigCompleted = () => {
			clearTimeout(timeoutId);
			eventBus.off('uds:calcConfig:completed', handleConfigCompleted);
			resolve();
		};

		eventBus.on('uds:calcConfig:completed', handleConfigCompleted);

		timeoutId = setTimeout(() => {
			eventBus.off('uds:calcConfig:completed', handleConfigCompleted);
			logger.error('Timeout waiting for uds:calcConfig:completed event');
			resolve();
		}, TIMEOUT_MS);
	});
};

/**
 * Custom hook to handle auto-opening AMD modal from SRP
 *
 * When user clicks AMD CTA on SRP, the URL may contain:
 * - Query params: ?autoOpen=true&route=credit (or dsLoad=true for legacy)
 * - Hash: #mmd-widget
 *
 * This hook detects those parameters and automatically opens the AMD modal
 *
 * Supported URL patterns:
 * - ?autoOpen=true&route=credit#mmd-widget (new pattern)
 * - ?autoOpen=true&dsLoad=true#mmd-widget (legacy pattern)
 *
 * @returns {void}
 */
const useAMDAutoOpen = () => {
	const [eventBus, isReady] = useEventBus();

	useEffect(() => {
		if (!isReady || hasExecuted) {
			return undefined;
		}

		let isMounted = true;

		try {
			const urlParams = new URLSearchParams(window.location.search);
			const { hash } = window.location;

			const autoOpen = urlParams.get('autoOpen') === 'true';
			const route = urlParams.get('route');
			const dsLoad = urlParams.get('dsLoad') === 'true';
			const hasMmdWidget = hash === '#mmd-widget';

			const shouldAutoOpen = autoOpen && hasMmdWidget && (dsLoad || route);

			if (shouldAutoOpen) {
				hasExecuted = true;
				scrollToUDS();

				(async () => {
					try {
						await waitForUDSConfigCompleted(eventBus);
						await waitForTogglesScript();
						
						// Check if component is still mounted before emitting
						if (!isMounted) return;
						
						// Only emit with route if it exists and is not empty
						if (route && route.trim()) {
							eventBus.emit('uds:openSdp:request', { route });
						} else if (dsLoad) {
							// Legacy dsLoad pattern without route
							eventBus.emit('uds:openSdp:request', {});
						}
					} catch (error) {
						if (isMounted) {
							logger.error('Failed to open AMD modal:', error);
						}
					}
				})();
			}
		} catch (error) {
			logger.error('AMD Auto-Open error:', error);
		}
		
		return () => {
			isMounted = false;
		};
	}, [isReady, eventBus]);
};

export default useAMDAutoOpen;
